"use client";
/**
 * Cross-browser preference sync.
 *
 * localStorage is per-browser, so a layout configured in Chrome never
 * appears in Firefox or on another machine. This module mirrors the
 * UI-preference subset of localStorage (`mpp:*`, minus the disposable
 * bar cache) to the server (`/api/prefs` → `user_prefs` table), so
 * the config follows the logged-in user everywhere.
 *
 * Model — the SERVER is canonical:
 *   - On app load, `hydratePrefs()` pulls every server pref and writes
 *     it into localStorage (raw, before any workspace/chart mounts).
 *   - `installPrefsSync()` registers a hook so every later synced
 *     write to localStorage is debounce-batched and PUT to the server.
 *   - On page hide, any pending batch is flushed via sendBeacon so a
 *     fast close doesn't drop the last change.
 *
 * The workspaces / chart keep reading localStorage synchronously and
 * need no changes — they just see server-hydrated values because the
 * <PrefsSyncGate> blocks render until hydration completes.
 */
import { setSyncedWriteHook, isSyncedKey, isPerMachineLayoutKey, isDeviceOnlyKey, clearDisposableCaches, purgeLegacyKeys, isLegacyKey } from "./safeLocalStorage";

const PREFS_ENDPOINT = "/api/prefs";
const FLUSH_DEBOUNCE_MS = 800;
/** Retry delay after a failed push — long enough not to hammer a down server,
 *  short enough that a blip doesn't sit unsynced until the next edit. */
const RETRY_MS = 5_000;
/**
 * Chrome caps the body of a `keepalive` fetch AND of `navigator.sendBeacon` at
 * 64 KiB, per document. Over that, `sendBeacon` returns false and a keepalive
 * fetch REJECTS ("Failed to fetch") — measured, not assumed. Several prefs are
 * tens of KB on their own (`mpp:advchart:study-templates:v1`,
 * `mpp:chart:templates:v1`, `mpp:workspaces:v1`), so a batch crosses it easily.
 * We stay under with headroom for the JSON envelope.
 *
 * The budget is also SHARED across in-flight beacons, not per request (measured:
 * two 43 KB beacons back to back — the second is refused). So chunking alone
 * isn't enough; a refused chunk must go back on `pending` for the retry, which
 * is what actually delivers it.
 */
const BEACON_LIMIT = 56_000;

// Pending key→value writes awaiting a server push. `null` = delete the
// server row (deletions are first-class: hydrate is server-canonical, so a
// removal that never reaches the server resurrects on the next load).
let pending: Record<string, string | null> = {};
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

function scheduleFlush(delay = FLUSH_DEBOUNCE_MS): void {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => void flush(), delay);
}

/** Split a batch into bodies small enough for a beacon. A single value that is
 *  itself over the limit gets its own chunk: it can never ride a beacon, but the
 *  in-page path (a plain fetch, which has NO cap) carries it fine.
 *  Exported for `npm run prefs:check`. */
export function chunkForBeacon(
  batch: Record<string, string | null>,
): Array<Record<string, string | null>> {
  const chunks: Array<Record<string, string | null>> = [];
  let cur: Record<string, string | null> = {};
  let size = 0;
  for (const [k, v] of Object.entries(batch)) {
    const cost = k.length + (v?.length ?? 4) + 8;
    if (size > 0 && size + cost > BEACON_LIMIT) {
      chunks.push(cur);
      cur = {};
      size = 0;
    }
    cur[k] = v;
    size += cost;
  }
  if (size > 0) chunks.push(cur);
  return chunks;
}

async function flush(): Promise<void> {
  flushTimer = null;
  const batch = pending;
  pending = {};
  if (Object.keys(batch).length === 0) return;
  try {
    // NO `keepalive` here. This is an ordinary in-page request — the page isn't
    // going anywhere — and keepalive would impose the 64 KiB body cap for
    // nothing. With it on, one oversized batch threw, got re-queued, grew as new
    // writes joined it, and wedged prefs sync for the rest of the session: every
    // later save looked fine locally and then came back as the server's older
    // copy on the next load, because hydratePrefs treats the server as canonical.
    // That is the "I pressed Save and it didn't persist" report.
    const res = await fetch(PREFS_ENDPOINT, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(batch),
    });
    if (!res.ok) throw new Error(`prefs PUT ${res.status}`);
  } catch {
    // Re-queue the failed batch and retry on a timer (newer pending writes for
    // the same key win). Without the retry a failure sat until the next edit.
    pending = { ...batch, ...pending };
    if (!flushTimer) scheduleFlush(RETRY_MS);
  }
}

/** Best-effort synchronous flush for page unload — sendBeacon survives
 *  the navigation/close that a normal fetch wouldn't. */
function beaconFlush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const batch = pending;
  pending = {};
  if (Object.keys(batch).length === 0) return;
  // Chunked: one 64 KiB-busting batch used to make sendBeacon return false, and
  // the keepalive fallback (capped the same way) reject — after which the batch
  // was simply dropped. "Prefs re-sync on next load" was wrong: the next load
  // hydrates the server's OLDER copy back DOWN, so a dropped flush doesn't delay
  // a change, it REVERTS it. Anything that can't be sent goes back on `pending`.
  for (const chunk of chunkForBeacon(batch)) {
    const body = JSON.stringify(chunk);
    let sent = false;
    try {
      // sendBeacon can't set the PUT method, but /api/prefs accepts POST-shaped
      // beacons for exactly this path.
      sent =
        typeof navigator !== "undefined" &&
        typeof navigator.sendBeacon === "function" &&
        body.length <= BEACON_LIMIT &&
        navigator.sendBeacon(PREFS_ENDPOINT, new Blob([body], { type: "application/json" }));
    } catch {
      sent = false;
    }
    if (sent) continue;
    // No beacon (unavailable, or a single value too big for one): keep it queued
    // so the next in-page flush retries it with an uncapped plain fetch. On a
    // visibilitychange the page is still alive, so that retry really happens.
    Object.assign(pending, chunk);
    if (body.length <= BEACON_LIMIT) {
      // Small enough to ride a keepalive fetch — worth one best-effort attempt in
      // case this really is an unload and nothing will run again. A rejection
      // here is expected (page torn down mid-flight); swallow it.
      void fetch(PREFS_ENDPOINT, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        /* still queued in `pending` — the next in-page flush retries it */
      });
    }
  }
  if (Object.keys(pending).length > 0) scheduleFlush(RETRY_MS);
}

/**
 * Push every pending synced write to the server NOW, instead of waiting out the
 * debounce. Call before a DELIBERATE reload (a Display Profile switch): the
 * reload's `hydratePrefs` treats the server as canonical, so anything written in
 * the last {@link FLUSH_DEBOUNCE_MS} would otherwise be pulled back to its older
 * server copy — a page you saved seconds before switching profiles would revert.
 * Uses the same beacon path as page-hide, so it survives the navigation.
 */
export function flushPrefsNow(): void {
  if (typeof window === "undefined") return;
  beaconFlush();
}

/**
 * Two-way merge of localStorage ⇆ the server's user_prefs. Call ONCE
 * on app load (while authenticated), before any component reads
 * localStorage.
 *
 *   - DOWN: every server pref is written into localStorage (the
 *     server is canonical for keys it already has).
 *   - UP: every *local-only* synced key — one present in localStorage
 *     but absent on the server — is uploaded. This is what migrates
 *     config that was saved BEFORE cross-browser sync existed (e.g.
 *     a workspace layout `mpp:daily-brief:v1`): it would otherwise
 *     stay trapped in the browser it was created in, because the
 *     sync write-hook only fires on NEW writes.
 *
 * Resolves even on failure — the app then runs on the local copy.
 */
/** Fired when hydratePrefs finishes — EVERY exit, including the offline /
 *  unauthenticated early returns. State that was read from localStorage before
 *  or during hydration (desk layouts, template libraries) re-reads on this, so
 *  a slow or briefly-failing /api/prefs (e.g. mid-deploy restart) degrades to
 *  "appears a moment later" instead of "empty until the next lucky reload". */
export const PREFS_HYDRATED_EVENT = "mpp:prefs:hydrated";

function announceHydrated(ok: boolean): void {
  try {
    window.dispatchEvent(new CustomEvent(PREFS_HYDRATED_EVENT, { detail: { ok } }));
  } catch {
    /* older browsers without CustomEvent constructor — nothing to announce */
  }
}

export async function hydratePrefs(): Promise<void> {
  if (typeof window === "undefined") return;

  // Reclaim space from dead key families BEFORE any down-sync write —
  // on a full origin the writes below all bounce and local state freezes
  // in the past while pushes keep working (the asymmetry that ate days).
  //
  // Purging LOCALLY is not enough for a synced key. Hydrate is
  // server-canonical, so step 3 below writes the server's copy straight back
  // into localStorage and the key resurrects on every single load — observed
  // exactly that way with `mpp:advchart:pine-dock:v1`. The row has to be
  // retired on the server too, which is what the `null` queued below does.
  purgeLegacyKeys();

  // 1. Pull the server's prefs.
  const serverPrefs: Record<string, string> = {};
  try {
    const res = await fetch(PREFS_ENDPOINT, { cache: "no-store" });
    if (!res.ok) {
      announceHydrated(false);
      return; // not authed / offline — run on local copy
    }
    const data = (await res.json()) as { prefs?: Record<string, unknown> };
    const prefs = data?.prefs;
    if (prefs && typeof prefs === "object") {
      for (const [k, v] of Object.entries(prefs)) {
        if (typeof k !== "string" || typeof v !== "string") continue;
        if (isLegacyKey(k)) {
          // Retired: drop it from what we are about to apply, and queue the
          // server row for deletion so the next flush finishes the job for
          // every other browser too. Without this the key is immortal — each
          // load purges it locally and then restores it from the server.
          pending[k] = null;
          scheduleFlush();
          continue;
        }
        serverPrefs[k] = v;
      }
    }
  } catch {
    announceHydrated(false);
    return; // offline — run on local copy
  }

  // 2. Find local-only synced keys (in localStorage, not on server).
  const uploads: Record<string, string> = {};
  const localKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && isSyncedKey(k)) localKeys.push(k);
  }
  for (const k of localKeys) {
    if (!(k in serverPrefs)) {
      const v = localStorage.getItem(k);
      if (v != null) uploads[k] = v;
    }
  }

  // 3. DOWN — write server prefs into localStorage (server canonical), EXCEPT
  //    per-machine layout state: another machine's pane sizes / arrangements
  //    must not overwrite this screen's own (legacy synced copies may still sit
  //    on the server from before this split — ignore them on the way down).
  // Quota parity with the save path: safeLocalStorageSet evicts the
  // disposable caches and retries when storage is full — this raw loop used
  // to silently SKIP instead. On a full origin that asymmetry meant every
  // outgoing save synced while every incoming server key was dropped for
  // days: local state froze in the past and each reload "reverted" work that
  // was safely on the server the whole time.
  let evicted = false;
  let failed = 0;
  for (const [k, v] of Object.entries(serverPrefs)) {
    if (isPerMachineLayoutKey(k)) continue;
    // A device-only key (the Real / Paper choice) is this device's alone, and
    // a retired key must not come back from the server's copy.
    if (isDeviceOnlyKey(k) || isLegacyKey(k)) continue;
    try {
      localStorage.setItem(k, v);
    } catch {
      if (!evicted) {
        evicted = true;
        try {
          clearDisposableCaches();
          localStorage.setItem(k, v);
          continue;
        } catch {
          /* still full — count it */
        }
      }
      failed += 1;
    }
  }
  if (failed > 0) {
    console.warn(
      `[prefs] hydrate could not write ${failed} server keys even after evicting caches — origin storage is full`,
    );
    try {
      void navigator.storage?.estimate?.().then((e) =>
        console.warn(
          `[prefs] storage estimate: usage=${Math.round((e.usage ?? 0) / 1048576)}MB quota=${Math.round((e.quota ?? 0) / 1048576)}MB`,
        ),
      );
    } catch {
      /* diagnostics only */
    }
  }

  // 4. UP — upload the local-only keys so they migrate to the server. No
  //    `keepalive`: this can be the whole local pref set on a first sync, far
  //    over the 64 KiB keepalive cap, and it runs during load with the page very
  //    much alive.
  if (Object.keys(uploads).length > 0) {
    try {
      await fetch(PREFS_ENDPOINT, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(uploads),
      });
    } catch {
      // Re-queue so the next debounced flush retries the upload.
      pending = { ...uploads, ...pending };
    }
  }

  announceHydrated(true);
}

/** Wire up the synced-write → server-push pipeline. Idempotent. */
export function installPrefsSync(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  setSyncedWriteHook((key, value) => {
    pending[key] = value;
    scheduleFlush();
  });

  // Belt-and-suspenders: many features persist synced `mpp:*` keys with a RAW
  // localStorage.setItem (chart templates, dashboard layout, index-chart
  // style, top-down universe, …) and bypass safeLocalStorageSet's hook. Since
  // hydratePrefs re-writes localStorage FROM the canonical server copy on
  // every reload, those writes were lost on refresh. Patch setItem so ANY
  // write to a synced key mirrors to the server. Installed AFTER hydration
  // (PrefsSyncGate calls hydratePrefs().finally(installPrefsSync)), so the
  // DOWN-hydration writes don't bounce straight back up. The bar cache isn't
  // a synced key, so the chart's heavy bar writes never hit the server.
  try {
    const ls = window.localStorage;
    const origSet = ls.setItem.bind(ls);
    ls.setItem = (k: string, v: string) => {
      origSet(k, v);
      const key = String(k);
      if (isSyncedKey(key)) {
        pending[key] = String(v);
        scheduleFlush();
      }
    };
  } catch {
    /* storage disabled — run on the local copy only */
  }

  window.addEventListener("pagehide", beaconFlush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") beaconFlush();
  });
}
